/**
 * Posts API helpers. UI → Helpers → API.
 */

import { compressImageForUpload } from './mediaCompress';
import { uploadFormData } from './uploadHelpers';

/**
 * Normalize GET /api/posts/:id JSON (envelope or legacy post-only body).
 * @returns {object|null}
 */
export function normalizePostDetailResponse(body) {
  if (!body || typeof body !== 'object') return null;
  if ('access' in body && Object.prototype.hasOwnProperty.call(body, 'post')) {
    return {
      access: body.access,
      post: body.post,
      poster: body.poster || null,
      comments_access: body.comments_access || 'full',
      follow_status: body.follow_status ?? null,
    };
  }
  if (body.post_id != null) {
    return {
      access: 'full',
      post: body,
      poster: null,
      comments_access: 'full',
      follow_status: null,
    };
  }
  return null;
}

export async function getPostById(postId) {
  const res = await fetch(`/api/posts/${postId}`, { credentials: 'include' });
  if (!res.ok) {
    if (res.status === 404) return null;
    throw new Error('Failed to fetch post');
  }
  const raw = await res.json();
  return normalizePostDetailResponse(raw);
}

export async function getPostFeed(
  limit = 20,
  offset = 0,
  feedMode = 'all',
  posterId = null,
  hashtag = null,
  locationId = null,
  eventId = null,
  profilePostsMode = null
) {
  const params = new URLSearchParams({ limit, offset, feed_mode: feedMode });
  if (posterId) params.set('poster_id', posterId);
  if (posterId && profilePostsMode === 'received') params.set('profile_posts_mode', 'received');
  if (hashtag) params.set('hashtag', hashtag);
  if (locationId) params.set('location_id', locationId);
  if (eventId) params.set('event_id', eventId);
  const res = await fetch(`/api/posts?${params}`, { credentials: 'include' });
  if (!res.ok) throw new Error('Failed to fetch feed');
  return res.json();
}

export async function createPost(
  content,
  mediaFile = null,
  mentionIds = [],
  location = null,
  eventIds = [],
  options = {}
) {
  const formData = new FormData();
  formData.append('content', content || '');
  if (mediaFile) {
    const compressed = await compressImageForUpload(mediaFile);
    formData.append('media', compressed);
  }
  if (mentionIds?.length) {
    formData.append('mention_ids', JSON.stringify(mentionIds));
  }
  if (eventIds?.length) {
    formData.append('event_ids', JSON.stringify(eventIds));
  }
  if (location) {
    if (location.location_id) formData.append('location_id', location.location_id);
    if (location.place_name) formData.append('place_name', location.place_name);
    if (location.short_description) formData.append('short_description', location.short_description);
    if (location.google_place_id) formData.append('google_place_id', location.google_place_id);
  }

  try {
    return await uploadFormData('/api/posts', formData, {
      onProgress: options.onProgress,
    });
  } catch (err) {
    throw new Error(err.message || 'Failed to create post');
  }
}

/**
 * Merge pending aura into post so feed shows aura count immediately without refresh.
 * Used when creating a post with pre-send aura in the composer. Each pending
 * entry carries a `points` count (defaults to 1 for legacy callers).
 */
export function mergePendingAuraIntoPost(post, pendingAura, auraCategories) {
  if (!pendingAura?.length) return post;
  const categoryMap = new Map((auraCategories || []).map((c) => [c.aura_category_id, c.category_name]));
  const mergedPost = { ...post };
  const mergedAuraSummary = [...(post.aura_summary || [])];

  const sumPoints = (predicate) =>
    pendingAura.reduce((acc, p) => (predicate(p) ? acc + (p.points ?? 1) : acc), 0);

  for (const { targetId, targetType, categoryId, hashtag, placeName, points } of pendingAura) {
    const categoryName = categoryMap.get(categoryId) || 'Aura';
    const addPoints = points ?? 1;
    let targetDisplay = '';
    if (targetType === 'user') {
      const m = post.mentions?.find((m) => m.user_id === targetId);
      targetDisplay = m?.username || 'user';
    } else if (targetType === 'hashtag') {
      targetDisplay =
        hashtag ||
        post.hashtags?.find((h) => h.hashtag_id === targetId)?.hashtag ||
        'tag';
    } else if (targetType === 'location') {
      targetDisplay = placeName || post.location?.place_name || 'location';
    }
    const existing = mergedAuraSummary.find(
      (s) =>
        s.category_name === categoryName &&
        s.target_type === targetType &&
        (targetType === 'user'
          ? s.target_username === targetDisplay
          : targetType === 'hashtag'
            ? s.target_hashtag === targetDisplay
            : s.target_place_name === targetDisplay)
    );
    if (existing) {
      existing.points += addPoints;
    } else {
      mergedAuraSummary.push({
        category_name: categoryName,
        points: addPoints,
        target_type: targetType,
        target_username: targetType === 'user' ? targetDisplay : undefined,
        target_hashtag: targetType === 'hashtag' ? targetDisplay : undefined,
        target_place_name: targetType === 'location' ? targetDisplay : undefined,
      });
    }
  }

  if (mergedPost.mentions) {
    mergedPost.mentions = mergedPost.mentions.map((m) => {
      const count = sumPoints((p) => p.targetType === 'user' && p.targetId === m.user_id);
      return { ...m, aura_count: (m.aura_count || 0) + count };
    });
  }
  if (mergedPost.hashtags) {
    mergedPost.hashtags = mergedPost.hashtags.map((h) => {
      const count = sumPoints(
        (p) =>
          p.targetType === 'hashtag' &&
          (p.targetId === h.hashtag_id ||
            p.hashtag?.toLowerCase() === h.hashtag?.toLowerCase())
      );
      return { ...h, aura_count: (h.aura_count || 0) + count };
    });
  }
  if (mergedPost.location) {
    const locCount = sumPoints(
      (p) =>
        p.targetType === 'location' &&
        (p.targetId === mergedPost.location.location_id ||
          (p.placeName && mergedPost.location.place_name?.toLowerCase() === p.placeName?.toLowerCase()))
    );
    mergedPost.location = {
      ...mergedPost.location,
      aura_count: (mergedPost.location.aura_count || 0) + locCount,
    };
  }
  mergedPost.aura_summary = mergedAuraSummary;
  return mergedPost;
}

export async function deletePost(postId) {
  const res = await fetch(`/api/posts/${postId}`, {
    method: 'DELETE',
    credentials: 'include',
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(body.error || 'Failed to delete post');
  return body;
}

/** Re-sphere a post (no aura). Returns updated counts from the server. */
export async function addResphere(postId) {
  const res = await fetch(`/api/posts/${postId}/resphere`, {
    method: 'POST',
    credentials: 'include',
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(body.error || 'Failed to re-sphere');
  return body;
}

export async function removeResphere(postId) {
  const res = await fetch(`/api/posts/${postId}/resphere`, {
    method: 'DELETE',
    credentials: 'include',
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(body.error || 'Failed to remove re-sphere');
  return body;
}

/** Submit a UGC report for a post; `reason` must be an allowed category code (Guideline 1.2-aligned). */
export async function reportPost(postId, { reason, details = '' } = {}) {
  const code = typeof reason === 'string' ? reason.trim() : '';
  const safeDetail =
    typeof details === 'string' ? details.trim().slice(0, 2000) : '';
  const res = await fetch(`/api/posts/${postId}/report`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ reason: code, details: safeDetail }),
    credentials: 'include',
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(body.error || 'Failed to report post');
  return body;
}

export async function toggleLike(itemId, itemType) {
  const res = await fetch('/api/likes', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ item_id: itemId, item_type: itemType }),
    credentials: 'include',
  });
  if (!res.ok) throw new Error('Failed to toggle like');
  return res.json();
}

export async function getComments(postId) {
  const res = await fetch(`/api/comments?post_id=${postId}`, { credentials: 'include' });
  if (!res.ok) throw new Error('Failed to fetch comments');
  return res.json();
}

export async function addComment(postId, content, mentionIds = [], location = null) {
  const body = { post_id: postId, comment_text: content || '' };
  if (mentionIds?.length) body.mention_ids = mentionIds;
  if (location) {
    if (location.location_id) body.location_id = location.location_id;
    if (location.place_name) body.place_name = location.place_name;
    if (location.short_description) body.short_description = location.short_description;
    if (location.google_place_id) body.google_place_id = location.google_place_id;
  }
  const res = await fetch('/api/comments', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
    credentials: 'include',
  });
  if (!res.ok) throw new Error('Failed to add comment');
  return res.json();
}

export async function getReplies(commentId) {
  const res = await fetch(`/api/replies?comment_id=${commentId}`, { credentials: 'include' });
  if (!res.ok) throw new Error('Failed to fetch replies');
  return res.json();
}

export async function addReply(commentId, replyText) {
  const res = await fetch('/api/replies', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ comment_id: commentId, reply_text: replyText }),
    credentials: 'include',
  });
  if (!res.ok) throw new Error('Failed to add reply');
  return res.json();
}
